<template>
    <Transition name="fade-slide-up" appear>
        <div
            v-if="displayLimit"
            class="premium-bg text-white br-8 vue-result-limits position-relative shadow mt-3 d-none d-sm-block"
        >
            <span
                class="position-absolute cursor-pointer d-block font-size-200 top-0 right-0 mt-4 mr-4 line-height-1ch fg-white"
                :class="{ 'opacity-65': isStateLoading }"
                @click.stop.prevent="dismiss()"
                >&times;</span
            >

            <div class="d-flex align-items-start flex-column p-4">
                <strong class="font-size-125 mb-4 w-100 pr-4">{{ limitTitle }}</strong>

                <span class="mb-5 vue-result-limits-description">{{ limitText }}</span>

                <div class="w-100 d-flex align-items-center justify-content-between vue-result-limits-button-container">
                    <a
                        @click.stop.prevent="dismiss()"
                        class="mr-4 text-white cursor-pointer hover-underline vue-result-limits-dismiss-text"
                        >{{ translate("No thanks, I don't want fast, unlimited access") }}</a
                    >

                    <ButtonWithLoadingState
                        :is-state-loading="isStateLoading"
                        :text="goPremiumText"
                        icon="m-l-5 fa fa-arrow-right"
                        :show-icon-after-text="true"
                        class="btn btn-warning font-weight-bold white-space-nowrap font-size-125 px-4 py-3"
                        @click.stop.prevent="doPremium"
                    ></ButtonWithLoadingState>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import ButtonWithLoadingState from '../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import { testIsArray, testIsNumber } from '../../../lib/test-and-assert/test-base';
import { LIMIT_FEATURE_TOO_MANY_JOBS, LIMIT_GLOBAL_TOO_MANY_JOBS } from '../../limits/limits';
import { translate } from '../../../qaamgo/helper/translate';
import TransFilter from '../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { pageStoreMixin } from '../../store/page-store';
import { setCookie } from '../../../qaamgo/helper/cookie';
import { COOKIE_RESULT_LIMIT_HINT_DISMISSED } from '../main-result';
import { globalLogger } from '../../../qaamgo/helper/global-logger';
import { satGlobals } from '../../../qaamgo/sat/helper/sat-globals';
import { registerPaymentModalHelper } from '../../user/modal/register_payment_flow/register-payment-modal-helper';
import { TOAST_HIDE, TOASTS } from '../../../qaamgo/modules/sat/components/toasts/toast-consts';
import { eventBus } from '../../../lib/event-bus/event-bus';

const secondsPerHour = 60 * 60;
const secondsPerDay = secondsPerHour * 24;

export default {
    name: TOASTS.TOAST_RESULT_LIMITS,
    components: { ButtonWithLoadingState },
    mixins: [TransFilter, pageStoreMixin],
    mounted() {
        this.operation = this.store.job.getOperation;

        const storeLimitsList = this.store.limits.getLimits;

        if (!testIsArray(storeLimitsList) || storeLimitsList.length === 0) {
            return;
        }

        const storeLimits = storeLimitsList[storeLimitsList.length - 1];

        this.displayLimit = this.getDisplayLimitFromStoreLimits(storeLimits);

        if (this.displayLimit !== null) {
            globalLogger.log('result limits - show toast');
        }
    },
    computed: {
        limitTitle() {
            if (!this.displayLimit) {
                return '';
            }

            let currentCount = 0;

            if (testIsNumber(this.displayLimit?.currentValue)) {
                currentCount = this.displayLimit.currentValue;
            }

            let maxCount = this.displayLimit.maxValue;

            // TODO add different text/handling for feature limits

            const limitTitleDaily = '%currentCount%/%maxCount% Free daily tasks';
            const limitTitleHours = '%currentCount%/%maxCount% Free tasks within %time% hours';
            const limitTitleOneHour = '%currentCount%/%maxCount% Free tasks within 1 hour';

            const limitTitleLimitHit = 'Your free tasks are up';
            const limitTitleLimitHitDaily = 'Your free daily tasks are up';

            let title = limitTitleDaily;

            const isHit = currentCount >= maxCount;
            const isDaily = this.displayLimit.ttl === secondsPerDay;
            const isHour = this.displayLimit.ttl === secondsPerHour;

            if (isHit) {
                if (isDaily) {
                    title = limitTitleLimitHitDaily;
                } else {
                    title = limitTitleLimitHit;
                }

                return translate(title);
            }

            if (isHour) {
                title = limitTitleOneHour;
            } else if (isDaily) {
                title = limitTitleDaily;
            } else {
                title = limitTitleHours;

                const ttlHours = Math.ceil(this.displayLimit.ttl / secondsPerHour);

                if (ttlHours === 1) {
                    title = limitTitleOneHour;
                } else {
                    return translate(title, {
                        '%currentCount%': currentCount,
                        '%maxCount%': maxCount,
                        '%time%': ttlHours.toString(),
                    });
                }
            }

            return translate(title, {
                '%currentCount%': currentCount,
                '%maxCount%': maxCount,
            });
        },
        limitText() {
            if (!this.displayLimit) {
                return '';
            }

            // TODO add different text/handling for feature limits
            // TODO allow different TTLs and display countdown

            const limitTextDaily = 'You get %count% free tasks a day. Upgrade your account to get unlimited access.';
            const limitTextHours =
                'You get %count% free tasks within %time% hours. Upgrade your account to get unlimited access.';
            const limitTextOneHour =
                'You get %count% free tasks within 1 hour. Upgrade your account to get unlimited access.';

            const isDaily = this.displayLimit.ttl === secondsPerDay;
            const isHour = this.displayLimit.ttl === secondsPerHour;

            let text = limitTextDaily;
            if (isHour) {
                text = limitTextOneHour;
            } else if (isDaily) {
                text = limitTextDaily;
            } else {
                text = limitTextHours;

                const ttlHours = Math.ceil(this.displayLimit.ttl / secondsPerHour);

                if (ttlHours === 1) {
                    text = limitTextOneHour;
                } else {
                    return translate(text, {
                        '%count%': this.displayLimit.maxValue,
                        '%time%': ttlHours.toString(),
                    });
                }
            }

            return translate(text, {
                '%count%': this.displayLimit.maxValue,
            });
        },
        goPremiumText() {
            if (satGlobals.getEnableFreeTrial()) {
                return translate('Try %count% days free', { '%count%': satGlobals.getFreeTrialPeriod() });
            }

            return translate('Go Premium');
        },
    },
    methods: {
        dismiss(hasShownModal) {
            if (!hasShownModal) {
                globalLogger.log('result limits - dismiss toast');

                // don't bother user for 1 week
                const cookieTtl = 60 * 60 * 24 * 7;
                setCookie(COOKIE_RESULT_LIMIT_HINT_DISMISSED, true, cookieTtl);
            }

            this.displayLimit = null;
            eventBus.emit(TOAST_HIDE, TOASTS.TOAST_RESULT_LIMITS);
        },
        doPremium() {
            this.isStateLoading = true;
            globalLogger.log('result limits - click get premium');

            registerPaymentModalHelper.openUpgrade().then(() => {
                this.isStateLoading = false;
                this.dismiss(true);
            });
        },
        getDisplayLimitFromStoreLimits(storeLimits) {
            if (storeLimits === null) {
                return null;
            }

            let featureLimit = null;
            let globalLimit = null;

            storeLimits.limits.forEach((/** LimitData */ limit) => {
                // TODO decide if we want to display conversion option limits, e.g. X ocr conversions per day
                // TODO for this to work we also need to check the conversion options for the current job
                if (limit.constraintType === 'conversion_option') {
                    return;
                }

                // TODO maybe prioritize limits by picking those that are closest to being hit

                if (limit.maxValue <= 0) {
                    return;
                }

                if (limit.type === LIMIT_FEATURE_TOO_MANY_JOBS && limit.triggeredBy === this.operation) {
                    featureLimit = limit;
                }

                if (limit.type === LIMIT_GLOBAL_TOO_MANY_JOBS) {
                    globalLimit = limit;
                }
            });

            // TODO this needs to be adjusted once we also start displaying feature limits here
            if (globalLimit !== null) {
                if (globalLimit.currentValue === null) {
                    // only show limit box if user has a conversion count
                    return null;
                }

                const conversionsLeft = globalLimit.maxValue - globalLimit.currentValue;

                // only show limit box if user has 3 or fewer conversions left
                if (conversionsLeft > 3) {
                    return null;
                }
            }

            // for now, we only want to display the global limit
            return globalLimit;

            // TODO handle feature-specific limits

            // TODO decide which limit to display/which limit is more immediate:
            // TODO A -> compare ttl/currentTtl and pick the one that takes longer to expire/be reset
            // TODO B -> just always prefer feature limit because it might be more relevant for the user
        },
    },
    data() {
        return {
            isStateLoading: false,
            operation: null,
            displayLimit: null,
        };
    },
};
</script>

<style lang="scss">
@use '~sass-material-colors/sass/sass-material-colors';

@use '../../../../scss/abstracts/mixins';
@use '../../../../scss/abstracts/functions';
@use '../../../../scss/abstracts/variables' as *;
@use 'sat-styles/abstracts/variables' as *;

//@use '~bootstrap/scss/input-group';

@use '../../../../scss/pages/authentication';
@use '../../../../scss/components/vue-transitions';

.vue-result-limits-dismiss-text {
    max-width: 200px;
}

.vue-result-limits-description {
    max-width: 450px;
}

@media (max-width: 530px) {
    .vue-result-limits {
        // toast offset is 30px bottom & 30px left
        margin-left: -30px;
        margin-bottom: -30px;
        width: 100vw;

        border-radius: 0 !important;
    }
}

@media (max-width: 410px) {
    .vue-result-limits {
        // reset margin because toast offset is 0 at this breakpoint
        margin-left: 0;
        margin-bottom: 0;
    }

    .vue-result-limits-description {
        margin-bottom: 1.5rem !important;
    }

    .vue-result-limits-button-container {
        text-align: center;
        flex-direction: column-reverse;
    }

    .vue-result-limits-dismiss-text {
        width: 100%;
        max-width: 100%;
        margin-right: 0 !important;
        margin-top: 1.5rem !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
    }
}
</style>
